import { useEffect, useState } from 'react';
import { archiveByKey, type Archive } from '../../api/archives';
import type { BinEntry } from '../../api/backend';
import { getItem } from '../../api/pandora';
import { posterUrl } from '../../engine/mediaurls';
import { formatTime } from '../../format/helpers';
import { concatRangeToPartRange } from '../../format/partRange';
import type { PandoraVideoSource } from '../../format/types';
import { useBin } from '../../state/bin';
import { usePlayback } from '../../state/playback';
import { useTimeline } from '../../state/timeline';
import ItemDetail, { type ItemPreset } from '../ItemDetail';
import './BinPanel.css';

/** External request to open an item's detail view inside the bin rail. */
export interface BinDetailRequest {
  archiveKey: string;
  itemId: string;
  preset?: ItemPreset;
}

interface Props {
  archives: Archive[];
  /** Set by EditorPage (e.g. from the advanced search overlay) to open a detail view. */
  detailRequest?: BinDetailRequest | null;
}

export default function BinPanel({ archives, detailRequest }: Props) {
  const entries = useBin((s) => s.entries);
  const [detail, setDetail] = useState<BinDetailRequest | null>(null);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (detailRequest) setDetail(detailRequest);
  }, [detailRequest]);

  async function addToTimeline(entry: BinEntry) {
    const archive = archiveByKey(archives, entry.archive);
    if (!archive) return;
    setError(null);
    try {
      const item = await getItem(archive.site, entry.item);
      const durations = item.durations ?? (item.duration ? [item.duration] : []);
      const { part, inPoint, outPoint } = concatRangeToPartRange(
        durations,
        entry.inPoint ?? 0,
        entry.outPoint ?? entry.duration,
      );
      const source: PandoraVideoSource = {
        kind: 'pandora-video',
        archive: archive.key,
        item: entry.item,
        part,
        duration: durations[part - 1] ?? entry.duration,
        title: entry.title,
        posterFrame: entry.posterFrame ?? 0,
        url: null,
      };
      useTimeline
        .getState()
        .addVideoClip(source, inPoint, outPoint, usePlayback.getState().currentTime);
      setAddedId(entry.id);
      setTimeout(() => setAddedId((id) => (id === entry.id ? null : id)), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load item');
    }
  }

  if (detail) {
    const archive = archiveByKey(archives, detail.archiveKey);
    if (archive) {
      return (
        <div className="bin-panel">
          <ItemDetail
            archive={archive}
            itemId={detail.itemId}
            preset={detail.preset}
            onBack={() => setDetail(null)}
          />
        </div>
      );
    }
  }

  return (
    <div className="bin-panel">
      <div className="bin-list">
        {error && <div className="search-error">{error}</div>}
        {entries.length === 0 && (
          <div className="search-hint">
            bin is empty — save clips here from search results or the item viewer
          </div>
        )}
        {entries.map((entry) => {
          const archive = archiveByKey(archives, entry.archive);
          const marked = entry.inPoint !== undefined && entry.outPoint !== undefined;
          return (
            <div key={entry.id} className="bin-card">
              <div className="bin-card-main">
                {archive && (
                  <img
                    className="result-poster"
                    src={posterUrl(archive, entry.item)}
                    alt=""
                    loading="lazy"
                  />
                )}
                <span className="result-info">
                  <span className="result-title">{entry.title || entry.item}</span>
                  <span className="result-meta timecode">
                    {formatTime(entry.duration)}
                    {entry.parts > 1 ? ` · ${entry.parts} parts` : ''}
                  </span>
                  {marked && (
                    <span className="bin-marks timecode">
                      {formatTime(entry.inPoint ?? 0)} – {formatTime(entry.outPoint ?? 0)}
                    </span>
                  )}
                </span>
              </div>
              <input
                className="bin-note"
                placeholder="add note…"
                value={entry.note ?? ''}
                onChange={(e) => useBin.getState().update(entry.id, { note: e.target.value })}
              />
              <div className="bin-card-actions">
                <button
                  disabled={!archive}
                  onClick={() =>
                    setDetail({
                      archiveKey: entry.archive,
                      itemId: entry.item,
                      preset: marked
                        ? { concatIn: entry.inPoint ?? 0, concatOut: entry.outPoint ?? 0 }
                        : undefined,
                    })
                  }
                >
                  Open
                </button>
                <button disabled={!archive} onClick={() => void addToTimeline(entry)}>
                  {addedId === entry.id ? 'Added ✓' : 'Add to timeline'}
                </button>
                <button
                  className="ghost danger bin-remove"
                  title="Remove from bin"
                  onClick={() => useBin.getState().remove(entry.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
